'use client';

import React, { useState } from 'react';
import { Share2, MessageCircle, Copy, Check, ExternalLink } from 'lucide-react';
import { COMPANY_ADDRESS } from '@/lib/company-address';

interface ProductShareBarProps {
  title: string;
  url: string;
  price?: string;
  imageUrl?: string;
  seller?: string;
}

export default function ProductShareBar({
  title,
  url,
  price,
  imageUrl,
  seller,
}: ProductShareBarProps) {
  const [copied, setCopied] = useState(false);

  const shareText = `${title}${price ? ` (${price})` : ''} on Parts4Drive - Guaranteed OEM fitment & corridor delivery. View: ${url}`;
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;

  const handleCopyLink = () => {
    if (typeof window !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleNativeShare = async () => {
    if (typeof window !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title,
          text: shareText,
          url,
        });
      } catch (err: unknown) {
        if ((err as Error)?.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 shadow-xs space-y-3.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
            <Share2 className="size-4" />
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-bold text-[#0A1B4A]">Share &amp; Broadcast SKU</h4>
            <p className="text-[11px] text-slate-500">Includes direct image preview &amp; fitment data</p>
          </div>
        </div>

        {/* Official Channel Pill */}
        <a
          href={COMPANY_ADDRESS.whatsappChannelUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 text-[11px] font-bold transition-all shadow-xs"
        >
          <MessageCircle className="size-3 text-emerald-600" />
          <span>Join Channel</span>
          <ExternalLink className="size-2.5 opacity-60" />
        </a>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
        <button
          type="button"
          onClick={handleNativeShare}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition cursor-pointer"
        >
          <Share2 className="size-3.5" />
          <span>Share</span>
        </button>

        <a
          href={whatsappShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#25D366] hover:bg-[#20ba59] text-white text-xs font-semibold transition shadow-xs cursor-pointer"
        >
          <MessageCircle className="size-3.5 fill-white text-[#25D366]" />
          <span>WhatsApp</span>
        </a>

        <a
          href={twitterShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold transition cursor-pointer"
        >
          <span>Twitter / X</span>
        </a>

        <button
          type="button"
          onClick={handleCopyLink}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold transition cursor-pointer"
        >
          {copied ? (
            <>
              <Check className="size-3.5 text-emerald-600" />
              <span className="text-emerald-700 font-bold">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="size-3.5" />
              <span>Copy Link</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
